import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format } from 'date-fns';
import { getAuditLogs, getAuditStats, getActionTypes, getAdmins } from '../../../api/auditApi';
import { useWeek } from '../../../contexts/WeekContext';
import AuditLogTable from './AuditLogTable';
import AuditStatsCards from './AuditStatsCards';
import AuditFilters from './AuditFilters';

const AuditLogTab = () => {
    const { dateRange } = useWeek();
    const [auditLogs, setAuditLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [actionTypes, setActionTypes] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filter states
    const [filters, setFilters] = useState({
        admin_user_id: '',
        action_type: '',
        startDate: '',
        endDate: '',
        page: 1,
        pageSize: 100
    });

    // Fetch initial data (action types and admins for dropdowns)
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [actionsData, adminsData] = await Promise.all([
                    getActionTypes(),
                    getAdmins()
                ]);
                setActionTypes(actionsData.actionTypes || []);
                setAdmins(adminsData.admins || []);
            } catch (err) {
                console.error('Failed to fetch dropdown data:', err);
            }
        };
        fetchInitialData();
    }, []);

    // Fetch audit logs when filters or date range changes
    useEffect(() => {
        if (dateRange.start && dateRange.end) {
            fetchAuditLogs();
        }
    }, [filters, dateRange.start, dateRange.end]);

    const fetchAuditLogs = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Use date range from WeekPicker context
            // Set start date to beginning of day (00:00:00)
            let startDate = '';
            if (dateRange.start) {
                const start = new Date(dateRange.start);
                start.setHours(0, 0, 0, 0);
                startDate = start.toISOString();
            }
            
            // Set end date to end of day (23:59:59)
            let endDate = '';
            if (dateRange.end) {
                const end = new Date(dateRange.end);
                end.setHours(23, 59, 59, 999);
                endDate = end.toISOString();
            }
            
            const filterParams = {
                ...filters,
                startDate: filters.startDate || startDate,
                endDate: filters.endDate || endDate
            };
            
            const [logsData, statsData] = await Promise.all([
                getAuditLogs(filterParams),
                getAuditStats(filterParams.startDate, filterParams.endDate)
            ]);
            
            setAuditLogs(logsData.logs || []);
            setStats(statsData);
        } catch (err) {
            console.error('Failed to fetch audit logs:', err);
            setError('Failed to load audit logs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters({ ...filters, ...newFilters, page: 1 });
    };

    const clearFilters = () => {
        setFilters({
            admin_user_id: '',
            action_type: '',
            startDate: '',
            endDate: '',
            page: 1,
            pageSize: 100
        });
    };

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            fetchAuditLogs();
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, [filters, dateRange]);

    if (loading && !auditLogs.length) {
        return (
            <div className="flex flex-col gap-4 w-full h-full">
                <div className="flex flex-row justify-between items-center">
                    <h3 className="subheader-textbox">Audit Logs</h3>
                </div>
                <div className="flex items-center justify-center flex-1">
                    <div className="flex flex-col items-center gap-3">
                        <Icon icon="eos-icons:loading" className="animate-spin text-4xl text-accent" />
                        <div className="text-font-secondary">Loading audit logs...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 w-full h-full overflow-y-auto overflow-x-hidden scrollable">
            <div className="flex flex-row justify-between items-center flex-shrink-0">
                <h3 className="subheader-textbox">Audit Logs</h3>
                <div className="text-sm text-font-secondary">
                    <span>
                        {dateRange.start && dateRange.end && (
                            <>
                                {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
                            </>
                        )}
                    </span>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-400 bg-opacity-20 border border-red-500 rounded-lg flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-font-primary text-sm font-medium">Error</span>
                    </div>
                    <p className="text-font-primary text-xs mt-1">{error}</p>
                </div>
            )}

            {/* Statistics Cards */}
            {stats && (
                <div className="flex-shrink-0">
                    <AuditStatsCards stats={stats} />
                </div>
            )}

            {/* Filters */}
            <div className="flex-shrink-0">
                <AuditFilters
                    filters={filters}
                    actionTypes={actionTypes}
                    admins={admins}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                />
            </div>

            {/* Audit Logs Table */}
            <div className="flex-shrink-0">
                <AuditLogTable
                    logs={auditLogs}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default AuditLogTab;

